import { ButtonLink } from "@ui/ButtonLink";
import { Loader } from "@ui/Loader";
import { Tooltip, TooltipTrigger } from "@ui/Tooltip";
import { formatMarkdown, htmlToPlainText, smartenText } from "@utils/text";
import { createUniqueId, type JSX, Show } from "solid-js";
import { questionActivity } from "~/modules/shared/utils/questionActivity";
import "./RecentQuestionLink.css";

type RecentQuestionTooltipProps = {
	/** Full question text, shown in the tooltip. */
	prompt: string;
	/** Receives the id to point the row's `aria-describedby` at. */
	children: (describedBy: string) => JSX.Element;
};

/**
 * Tooltip showing the full question for a sidebar row that only has space for a
 * truncated label. Prompts are written as Markdown, so the tooltip renders them
 * rich while the off-screen copy — which gives assistive tech the description the
 * native `title` attribute used to provide — is flattened back to plain text.
 *
 * `formatMarkdown` sanitises its output and applies smart quotes itself, so the
 * rendered prompt needs neither `smartenText` nor the `Tooltip` `smarten` prop.
 */
export const RecentQuestionTooltip = (props: RecentQuestionTooltipProps) => {
	const promptId = createUniqueId();
	const promptHtml = () => formatMarkdown(props.prompt);
	const spokenPrompt = () => {
		const container = document.createElement("div");
		container.innerHTML = promptHtml();
		return htmlToPlainText(container).trim();
	};
	return (
		<>
			<TooltipTrigger
				class="recent-question-trigger"
				placement="right"
				delay={1000}
				renderContent={() => (
					<Tooltip width="wide">
						<div class="recent-question-tooltip" innerHTML={promptHtml()} />
					</Tooltip>
				)}
			>
				{props.children(promptId)}
			</TooltipTrigger>
			<span id={promptId} class="recent-question-description">
				{spokenPrompt()}
			</span>
		</>
	);
};

type RecentQuestionLabelProps = {
	questionId: string;
	/** Full question text, used when there is no summary yet. */
	prompt: string;
	/** Shorter generated title, replacing the prompt once it exists. */
	summary?: string;
};

/**
 * Row text for a recent question. The data attributes let `ChatLayout` swap the
 * prompt for a summary once one has been generated.
 */
export const RecentQuestionLabel = (props: RecentQuestionLabelProps) => {
	const step = () => questionActivity(props.questionId);
	return (
		<span class="recent-question-row">
			<span
				class="menu-item-label"
				data-question-summary-target="true"
				data-question-id={props.questionId}
			>
				{smartenText(props.summary?.trim() || props.prompt)}
			</span>
			<Show when={step()}>
				{(current) => (
					<span
						class="recent-question-running"
						role="status"
						title={current().title || "Running"}
					>
						<Loader size="xsmall" />
					</span>
				)}
			</Show>
		</span>
	);
};

type RecentQuestionLinkProps = RecentQuestionLabelProps & {
	href: string;
	/** Tooltip text, when something reads better than the raw prompt. */
	tooltip?: string;
};

export const RecentQuestionLink = (props: RecentQuestionLinkProps) => (
	<li>
		<RecentQuestionTooltip prompt={props.tooltip?.trim() || props.prompt}>
			{(describedBy) => (
				<ButtonLink
					href={props.href}
					variant="text"
					ariaDescribedBy={describedBy}
					theme="monochrome"
					fullWidth
				>
					<RecentQuestionLabel
						questionId={props.questionId}
						prompt={props.prompt}
						summary={props.summary}
					/>
				</ButtonLink>
			)}
		</RecentQuestionTooltip>
	</li>
);
